import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Analytics } from "aws-amplify";
import { ClipLoader } from "react-spinners";

import "../../exam/components/TestScreen.css";

import ExerciseHeader from "./ExerciseHeader";
import ExerciseSectionTabs from "./ExerciseSectionTabs";
import ExerciseQuestionCard from "./ExerciseQuestionCard";
import ExerciseFooter from "./ExerciseFooter";

import SubmitModal from "../../../../shared/components/SubmitModal/SubmitModal";
import Modal from "../../../../shared/components/Modal/Modal";
import RewardModal from "../../../RewardModal/RewardModal";
import CoinUsesModal from "../../../CoinUsesModal/CoinUsesModal";
import images from "../../../../utilities/images/images";

import uiActions from "../../../../redux/ui/action";
import userTestsExercisesAction from "../../../../redux/user_tests_exercises/action";
import userPyqTestsAction from "../../../../redux/pyq_test_exercises/action";

import {
  applyExerciseProgress,
  applyQuestionAnswerEvaluation,
  buildTopicExerciseSubmitSummary,
  clearExerciseProgress,
  getExerciseProgressParams,
  getPyqProgressType,
  markExerciseAsSubmitted,
  persistExerciseProgress,
  persistExerciseSummary,
  readExerciseProgress,
  resolveResumeQuestionIndex,
  withResolvedPaperId,
} from "../../../../containers/logged_user_components/evaluation_components/exercise_related_components/exercise_Test_component/exerciseTest_content_component/exercisePaperIdUtils";

import {
  DPP_FLOW,
  HOMEWORK_FLOW,
  PYQ_FLOW,
  fetchCoinUsesPoints,
  fetchFlowQuestionList,
  fetchHomeworkSingleQuestion,
  fetchPyqSingleQuestion,
  fetchSubmissionCoins,
  resolveFlowType,
  resolveHomeworkFormat,
  saveFlowQuestion,
  submitHomeworkPaper,
  submitPyqPaper,
} from "../services/exerciseExamApi";

import {
  buildExerciseLiveProgressIdentity,
  buildPyqPracticeSummary,
  clearExerciseLiveProgress,
  getExerciseAnswerTypeId,
  getExerciseQuestionStats,
  getExerciseSectionKey,
  getExerciseSections,
  getHomeworkPaperId,
  hasCommittedExerciseAnswer,
  hasExerciseAnswer,
  mergeHomeworkQuestionDetails,
  mergePyqQuestionDetails,
  normalizeExerciseExamQuestions,
  persistExerciseLiveProgress,
  persistHomeworkPracticeSummary,
  persistHomeworkResumeProgress,
  persistPyqPracticeSummary,
  readExerciseLiveProgress,
  readHomeworkResumeProgress,
  clearHomeworkResumeProgress,
  withCommittedExerciseAnswer,
} from "../utils/exerciseExamUtils";
import { getTopicFlowRoutes, isTopicComponentPath } from "../utils/topicFlowRoutes";

const AUTO_SUBMIT_DELAY_MS = 2200;

const FLOW_LABELS = {
  exercise: "Exercise",
  dpp: "DPP",
  pyq: "PYQ",
  homework: "Homework",
};

const ExerciseTestScreen = (props) => {
  const {
    exerciseData,
    pyqTestData,
    homeworkTestDetails,
    jeeMainOrAdvance,
    topicData,
    subjectData,
    userPreference,
    userData,
    startLoading,
    finishLoading,
    setUserTestsResultData,
    setPyqTestsResultData,
  } = props;

  const history = useHistory();
  const location = useLocation();

  const testType = resolveFlowType(location?.state, location?.pathname);
  const isPyq = testType === PYQ_FLOW;
  const isHomework = testType === HOMEWORK_FLOW;
  const isResumeFlow = location?.state?.isResume === true;
  // Topic Exercise, DPP, PYQ, and homework run as practice so answers can be checked.
  const isPracticeMode =
    isHomework || isTopicComponentPath(location?.pathname);
  const flowLabel = FLOW_LABELS[testType] || "Test";

  const testData = isPyq
    ? pyqTestData
    : isHomework
      ? homeworkTestDetails
      : exerciseData;

  const [testDetails, setTestDetails] = useState({});
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentLang, setCurrentLang] = useState("en");
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [isQuestionDetailLoading, setIsQuestionDetailLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isSubmitModalOpen, setSubmitModalOpen] = useState(false);
  const [isExitModalOpen, setExitModalOpen] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [coinPopup, setCoinPopup] = useState(false);
  const [coinDetailsPopup, setCoinDetailsPopup] = useState(false);
  const [earnedCoins, setEarnedCoins] = useState(0);
  const [coinReason, setCoinReason] = useState("RewardModal");
  const [coinUsesPoints, setCoinUsesPoints] = useState([]);

  const questionStartRef = useRef(Date.now());
  const hasAutoSubmittedRef = useRef(false);
  const autoSubmitTimeoutRef = useRef(null);
  const submitHandlerRef = useRef(null);
  const submittedSummaryRef = useRef(null);
  const questionsRef = useRef([]);
  const currentIndexRef = useRef(0);
  const timerSecondsRef = useRef(0);
  const shouldPersistRef = useRef(true);

  const durationSeconds = Number(testData?.duration_mins) * 60 || 0;
  const isCountdown = durationSeconds > 0;

  const returnTo =
    testData?.returnTo ||
    location?.state?.returnTo ||
    (isHomework ? "/homework" : "/topic_component");
  const returnState = testData?.returnState ?? location?.state?.returnState;

  // Topic exercise/DPP also keep a keyed localStorage snapshot. A same-tab
  // live snapshot covers sudden refresh for exercise, DPP, and PYQ.
  const progressParams = useMemo(() => {
    if (isHomework) return null;

    if (isPyq) {
      const exerciseType = getPyqProgressType(testData || {});
      if (!exerciseType) return null;

      return {
        userId: userData?.user_id,
        courseId: userPreference?.courseDetails?.[0]?.course_id,
        topicId: topicData?.topicId,
        exerciseType,
      };
    }

    return getExerciseProgressParams({
      testDetails: testData || {},
      userData,
      userPreference,
      topicData,
    });
  }, [isPyq, isHomework, testData, userData, userPreference, topicData]);

  const liveProgressIdentity = useMemo(
    () =>
      buildExerciseLiveProgressIdentity({
        testType,
        testDetails: testData || {},
        topicData,
      }),
    [testType, testData, topicData]
  );

  questionsRef.current = questions;
  currentIndexRef.current = currentIndex;
  timerSecondsRef.current = timerSeconds;

  const currentQuestion = questions[currentIndex] || null;
  const sections = useMemo(() => getExerciseSections(questions), [questions]);
  const activeSection = currentQuestion
    ? getExerciseSectionKey(currentQuestion)
    : sections[0];
  const stats = useMemo(
    () => getExerciseQuestionStats(questions, currentIndex),
    [questions, currentIndex]
  );

  const markedForReviewCount = stats.marked + stats.answeredMarked;
  const attemptedCount = stats.answered;
  const notAttemptedCount = Math.max(
    questions.length - attemptedCount - markedForReviewCount,
    0
  );

  /* ======================= LOAD QUESTIONS ======================= */
  useEffect(() => {
    let isActive = true;

    const loadQuestions = async () => {
      const hasTestContext = isPyq
        ? Boolean(testData?.topic_name)
        : isHomework
          ? Boolean(testData?.id || testData?.paper_id)
          : Boolean(testData?.excersie_type);

      if (!hasTestContext) {
        history.replace(returnTo);
        return;
      }

      setIsLoadingQuestions(true);
      finishLoading();

      try {
        const result = await fetchFlowQuestionList({
          testType,
          testDetails: testData,
          userPreference,
          topicData,
          jeeMainOrAdvance,
        });

        if (!isActive) return;

        if (!result.ok) {
          alert(result.message || "Unable to load questions.");
          history.replace(isPyq || isHomework ? returnTo : "/store");
          return;
        }

        let normalizedQuestions = normalizeExerciseExamQuestions(
          result.questions,
          result.paperId
        );

        if (isHomework) {
          normalizedQuestions = normalizedQuestions.map((question) => ({
            ...question,
            detailsFetched: false,
          }));
        }

        let resumeIndex = Number(result.resumeIndex) || 0;
        const sessionProgress = readExerciseLiveProgress(liveProgressIdentity);
        const homeworkResume = isHomework
          ? readHomeworkResumeProgress(getHomeworkPaperId(testData))
          : null;
        const savedProgress =
          (sessionProgress && homeworkResume
            ? (sessionProgress.updatedAt || 0) >= (homeworkResume.updatedAt || 0)
              ? sessionProgress
              : homeworkResume
            : sessionProgress || homeworkResume) ||
          (progressParams ? readExerciseProgress(progressParams) : null);

        const shouldRestoreProgress = Boolean(savedProgress);

        if (shouldRestoreProgress) {
          normalizedQuestions = applyExerciseProgress(
            normalizedQuestions,
            savedProgress
          ).map((question, index) => ({
            ...question,
            visited:
              question.visited === true ||
              hasExerciseAnswer(question) ||
              Boolean(question.marked) ||
              index <= (savedProgress.currentQuestion ?? 0),
          }));
          resumeIndex = resolveResumeQuestionIndex({
            savedProgress,
            apiResumeIndex: Number(result.resumeIndex) || 0,
            totalQuestions: normalizedQuestions.length,
          });
        } else if (resumeIndex > 0) {
          normalizedQuestions = normalizedQuestions.map((question, index) => ({
            ...question,
            visited: index <= resumeIndex,
          }));
        }

        if (!isPyq && !isHomework) {
          Analytics.record({
            name:
              testType === DPP_FLOW
                ? "web_dpp_start_test"
                : "web_exercise_start_test",
            attributes: {
              ...(testType === DPP_FLOW
                ? {
                    dpp_type_Name: testData.excersie_type,
                    video_id: testData.videoId,
                  }
                : { Exercise_type_Name: testData.excersie_type }),
              topic_id: topicData?.topicId,
              topic_name: topicData?.topicName,
              subject_id: subjectData?.subjectId,
              subject_name: subjectData?.subjectName,
            },
          });
        }

        setTestDetails(withResolvedPaperId(testData, result.paperId));
        setQuestions(normalizedQuestions);
        setCurrentIndex(resumeIndex);

        const savedTimer = Number(savedProgress?.timerSeconds);
        const hasSavedTimer =
          shouldRestoreProgress && Number.isFinite(savedTimer) && savedTimer >= 0;
        setTimerSeconds(
          hasSavedTimer
            ? isCountdown
              ? Math.min(savedTimer, durationSeconds || savedTimer)
              : savedTimer
            : isCountdown
              ? Math.max(durationSeconds - result.usedTime, 0)
              : result.usedTime
        );
        questionStartRef.current = Date.now();
      } catch (error) {
        console.log(error);
        if (isActive) {
          history.replace(returnTo);
        }
      } finally {
        if (isActive) {
          setIsLoadingQuestions(false);
        }
        finishLoading();
      }
    };

    loadQuestions();

    return () => {
      isActive = false;
    };
    // Questions are fetched once per test entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPyq || isHomework) return;

    const loadCoinUses = async () => {
      try {
        setCoinUsesPoints(await fetchCoinUsesPoints());
      } catch (error) {
        console.log(error);
      }
    };

    loadCoinUses();
  }, [isPyq, isHomework]);

  /* ======================= TIMER ======================= */
  useEffect(() => {
    if (!questions.length) return undefined;

    const intervalId = setInterval(() => {
      setTimerSeconds((previous) =>
        isCountdown ? Math.max(previous - 1, 0) : previous + 1
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, [questions.length, isCountdown]);

  useEffect(() => {
    if (!isCountdown || !questions.length) return;
    if (timerSeconds > 0 || hasAutoSubmittedRef.current) return;

    hasAutoSubmittedRef.current = true;
    setShowTimeUpModal(true);
    autoSubmitTimeoutRef.current = setTimeout(() => {
      submitHandlerRef.current?.();
    }, AUTO_SUBMIT_DELAY_MS);
  }, [timerSeconds, isCountdown, questions.length]);

  useEffect(() => {
    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }
    };
  }, []);

  /* ======================= VISITED TRACKING ======================= */
  useEffect(() => {
    if (!questions.length) return;
    if (questions[currentIndex]?.visited) return;

    setQuestions((previous) =>
      previous.map((question, index) =>
        index === currentIndex ? { ...question, visited: true } : question
      )
    );
  }, [currentIndex, questions]);

  // PYQ and homework list APIs omit the option payload. The current question
  // is loaded from the single-question endpoint for that paper and qid.
  useEffect(() => {
    if (!isPyq && !isHomework) return;

    const question = questions[currentIndex];
    const paperId =
      question?.paper_id ?? testDetails?.paper_id ?? testDetails?.id ?? testData?.id;
    if (!question?.qid || !paperId || question.detailsFetched) {
      return undefined;
    }

    let isActive = true;
    setIsQuestionDetailLoading(true);

    const loadQuestionDetails = async () => {
      try {
        const details = isHomework
          ? await fetchHomeworkSingleQuestion({
              question: { ...question, paper_id: paperId },
              format: resolveHomeworkFormat(testData),
            })
          : await fetchPyqSingleQuestion(paperId, question.qid);
        if (!isActive) return;

        setQuestions((previous) =>
          previous.map((item) => {
            if (String(item.qid) !== String(question.qid)) return item;
            return isHomework
              ? mergeHomeworkQuestionDetails(item, details)
              : mergePyqQuestionDetails(item, details, {
                  applyBackendUserAnswers: isResumeFlow,
                });
          })
        );
      } catch (error) {
        console.log(error);
        if (!isActive) return;
        setQuestions((previous) =>
          previous.map((item) =>
            String(item.qid) === String(question.qid)
              ? { ...item, detailsFetched: true }
              : item
          )
        );
      } finally {
        if (isActive) {
          setIsQuestionDetailLoading(false);
        }
      }
    };

    loadQuestionDetails();

    return () => {
      isActive = false;
    };
  }, [
    isPyq,
    isHomework,
    currentIndex,
    questions[currentIndex]?.qid,
    questions[currentIndex]?.paper_id,
    questions[currentIndex]?.detailsFetched,
    testDetails?.paper_id,
    testDetails?.id,
    testData,
    isResumeFlow,
  ]);

  /* ======================= HELPERS ======================= */
  const persistProgress = useCallback(
    (questionList, questionIndex) => {
      if (!shouldPersistRef.current) return;

      const nextQuestions = questionList || [];
      const nextIndex = questionIndex ?? 0;
      const nextTimer = timerSecondsRef.current;

      if (progressParams) {
        persistExerciseProgress({
          ...progressParams,
          questions: nextQuestions,
          currentQuestion: nextIndex,
          timerSeconds: nextTimer,
        });
      }

      persistExerciseLiveProgress({
        identity: liveProgressIdentity,
        questions: nextQuestions,
        currentQuestion: nextIndex,
        timerSeconds: nextTimer,
      });

      if (isHomework) {
        persistHomeworkResumeProgress({
          paperId:
            getHomeworkPaperId(testData) || getHomeworkPaperId(testDetails),
          currentQuestion: nextIndex,
          timerSeconds: nextTimer,
          questions: nextQuestions,
        });
      }
    },
    [progressParams, liveProgressIdentity, isHomework, testData, testDetails]
  );

  useEffect(() => {
    if (isLoadingQuestions || !questions.length) return;
    persistProgress(questions, currentIndex);
  }, [questions, currentIndex, isLoadingQuestions, persistProgress]);

  useEffect(() => {
    if (isLoadingQuestions || !questions.length) return undefined;

    const intervalId = setInterval(() => {
      persistProgress(questionsRef.current, currentIndexRef.current);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isLoadingQuestions, questions.length, persistProgress]);

  useEffect(() => {
    const persistLatest = () => {
      if (!questionsRef.current.length) return;
      persistProgress(questionsRef.current, currentIndexRef.current);
    };

    window.addEventListener("pagehide", persistLatest);
    window.addEventListener("beforeunload", persistLatest);

    return () => {
      persistLatest();
      window.removeEventListener("pagehide", persistLatest);
      window.removeEventListener("beforeunload", persistLatest);
    };
  }, [persistProgress]);

  const saveQuestion = useCallback(
    async (question) => {
      if (!question) return;

      try {
        await saveFlowQuestion({
          testType,
          testDetails,
          question,
          userPreference,
          topicData,
          userData,
          language: currentLang,
        });
      } catch (error) {
        console.log(error);
      }
    },
    [testType, testDetails, userPreference, topicData, userData, currentLang]
  );

  // Rolls the elapsed time of the on-screen question into its total and
  // returns the updated list so callers can save/persist in one pass.
  const commitElapsedTime = useCallback((questionList, questionIndex) => {
    const elapsedSeconds = Math.max(
      Math.round((Date.now() - questionStartRef.current) / 1000),
      0
    );
    questionStartRef.current = Date.now();

    return questionList.map((question, index) =>
      index === questionIndex
        ? {
            ...question,
            totalTimeTaken: (question.totalTimeTaken || 0) + elapsedSeconds,
          }
        : question
    );
  }, []);

  const moveToQuestion = useCallback(
    (nextIndex) => {
      if (nextIndex < 0 || nextIndex >= questions.length) return;
      if (nextIndex === currentIndex) return;

      const updatedQuestions = commitElapsedTime(questions, currentIndex);
      const leavingQuestion = updatedQuestions[currentIndex];

      if (
        hasCommittedExerciseAnswer(leavingQuestion) ||
        leavingQuestion?.marked
      ) {
        saveQuestion(leavingQuestion);
      }

      setQuestions(updatedQuestions);
      setCurrentIndex(nextIndex);
      persistProgress(updatedQuestions, nextIndex);
      window.scrollTo(0, 0);
    },
    [questions, currentIndex, commitElapsedTime, saveQuestion, persistProgress]
  );

  /* ======================= ANSWER HANDLERS ======================= */
  const handleSelectOption = useCallback(
    (optionId) => {
      setQuestions((previous) =>
        previous.map((question, index) => {
          if (index !== currentIndex) return question;
          if (question.answerChecked) return question;

          const answerTypeId = getExerciseAnswerTypeId(question);
          const existingAnswers = question.userAnswerCollection || [];
          const alreadySelected = existingAnswers.some(
            (answer) => String(answer.option_id) === String(optionId)
          );

          let userAnswerCollection;
          if (answerTypeId === 2) {
            userAnswerCollection = alreadySelected
              ? existingAnswers.filter(
                  (answer) => String(answer.option_id) !== String(optionId)
                )
              : [...existingAnswers, { option_id: optionId }];
          } else {
            userAnswerCollection = alreadySelected
              ? []
              : [{ option_id: optionId }];
          }

          return applyQuestionAnswerEvaluation({
            ...question,
            userAnswerCollection,
            is_saved: Number(question.is_saved) === 1 ? 1 : 0,
          });
        })
      );
    },
    [currentIndex]
  );

  const handleIntegerAnswerChange = useCallback(
    (value) => {
      setQuestions((previous) =>
        previous.map((question, index) =>
          index === currentIndex && !question.answerChecked
            ? applyQuestionAnswerEvaluation({
                ...question,
                integer_answer: value,
                is_saved: Number(question.is_saved) === 1 ? 1 : 0,
              })
            : question
        )
      );
    },
    [currentIndex]
  );

  const handleToggleBookmark = useCallback(() => {
    setQuestions((previous) =>
      previous.map((question, index) =>
        index === currentIndex
          ? { ...question, marked: !question.marked }
          : question
      )
    );
  }, [currentIndex]);

  const handleToggleLanguage = useCallback(() => {
    setCurrentLang((previous) => (previous === "en" ? "hi" : "en"));
  }, []);

  /* ======================= NAVIGATION HANDLERS ======================= */
  const handleSaveAndNext = useCallback(() => {
    const timedQuestions = commitElapsedTime(questions, currentIndex);
    const updatedQuestions = timedQuestions.map((question, index) =>
      index === currentIndex ? withCommittedExerciseAnswer(question) : question
    );
    const nextIndex = Math.min(currentIndex + 1, questions.length - 1);

    saveQuestion(updatedQuestions[currentIndex]);
    setQuestions(updatedQuestions);
    setCurrentIndex(nextIndex);
    persistProgress(updatedQuestions, nextIndex);
    window.scrollTo(0, 0);
  }, [questions, currentIndex, commitElapsedTime, saveQuestion, persistProgress]);

  const handleMarkReview = useCallback(() => {
    const timedQuestions = commitElapsedTime(questions, currentIndex);
    const updatedQuestions = timedQuestions.map((question, index) => {
      if (index !== currentIndex) return question;
      const markedQuestion = { ...question, marked: true };
      return hasExerciseAnswer(markedQuestion)
        ? withCommittedExerciseAnswer(markedQuestion)
        : markedQuestion;
    });
    const nextIndex = Math.min(currentIndex + 1, questions.length - 1);

    saveQuestion(updatedQuestions[currentIndex]);
    setQuestions(updatedQuestions);
    setCurrentIndex(nextIndex);
    persistProgress(updatedQuestions, nextIndex);
    window.scrollTo(0, 0);
  }, [questions, currentIndex, commitElapsedTime, saveQuestion, persistProgress]);

  const handlePracticeAction = useCallback(() => {
    const question = questions[currentIndex];
    if (!question) return;

    if (question.answerChecked) {
      setQuestions((previous) =>
        previous.map((item, index) =>
          index === currentIndex ? { ...item, showSolution: true } : item
        )
      );
      return;
    }

    if (!hasExerciseAnswer(question)) return;

    const timedQuestions = commitElapsedTime(questions, currentIndex);
    const updatedQuestions = timedQuestions.map((item, index) =>
      index === currentIndex
        ? {
            ...withCommittedExerciseAnswer(item),
            answerChecked: true,
            showSolution: false,
          }
        : item
    );

    saveQuestion(updatedQuestions[currentIndex]);
    setQuestions(updatedQuestions);
    persistProgress(updatedQuestions, currentIndex);
  }, [
    questions,
    currentIndex,
    commitElapsedTime,
    saveQuestion,
    persistProgress,
  ]);

  const handlePrevious = useCallback(() => {
    moveToQuestion(currentIndex - 1);
  }, [moveToQuestion, currentIndex]);

  const handleSectionChange = useCallback(
    (section) => {
      const targetIndex = questions.findIndex(
        (question) => getExerciseSectionKey(question) === section
      );
      if (targetIndex >= 0) {
        moveToQuestion(targetIndex);
      }
    },
    [questions, moveToQuestion]
  );

  /* ======================= SUBMIT / EXIT ======================= */
  const navigateToExerciseSummary = useCallback(
    (localSummary) => {
      const flowRoutes = getTopicFlowRoutes(testType, location.pathname);
      history.replace({
        pathname: flowRoutes.summary,
        state: {
          paper_id: null,
          localSummary,
          returnTo,
          returnState,
        },
      });
    },
    [history, testType, location.pathname, returnTo, returnState]
  );

  const recordSubmitAnalytics = useCallback(() => {
    try {
      Analytics.record({
        name:
          testType === DPP_FLOW
            ? "web_dpp_submit_test"
            : "web_exercise_submit_test",
        attributes: {
          ...(testType === DPP_FLOW
            ? {
                dpp_type_Name: testDetails.excersie_type,
                video_id: testDetails.videoId,
              }
            : { Exercise_type_Name: testDetails.excersie_type }),
          topic_id: topicData?.topicId,
          topic_name: topicData?.topicName,
          subject_id: subjectData?.subjectId,
          subject_name: subjectData?.subjectName,
        },
      });
    } catch (error) {
      console.log(error);
    }
  }, [testType, testDetails, topicData, subjectData]);

  const submitPyq = useCallback(
    async (updatedQuestions, elapsedTime) => {
      const paperId =
        updatedQuestions[currentIndex]?.paper_id ?? testDetails?.paper_id ?? null;

      if (!paperId) {
        alert("Paper id is required to submit this test.");
        return;
      }

      const submitted = await submitPyqPaper(paperId, elapsedTime);
      if (!submitted) {
        alert("Something went wrong while submitting. Please try again.");
        return;
      }

      setPyqTestsResultData({
        topic_wise: true,
        ...testDetails,
        paper_id: paperId,
        topic_id:
          testDetails?.topic_id ??
          updatedQuestions[currentIndex]?.topic_id ??
          null,
        subject: testDetails?.subject ?? null,
        format: testDetails?.format ?? jeeMainOrAdvance ?? null,
        returnTo,
        returnState,
      });

      const timeSpentSeconds = isCountdown
        ? Math.max(durationSeconds - timerSeconds, 0)
        : timerSeconds;
      const summary = buildPyqPracticeSummary({
        questions: updatedQuestions,
        testDetails: { ...testDetails, returnTo, returnState },
        timeSpentSeconds,
        paperId,
      });
      persistPyqPracticeSummary(summary);
      shouldPersistRef.current = false;
      clearExerciseLiveProgress(liveProgressIdentity);

      const flowRoutes = getTopicFlowRoutes(PYQ_FLOW, location.pathname);
      history.replace({
        pathname: flowRoutes.summary,
        state: {
          tab: "pyq",
          paper_id: paperId,
          returnTo,
          returnState,
          source: testDetails?.redirectedFrom || "activity",
          summary,
        },
      });
    },
    [
      currentIndex,
      testDetails,
      jeeMainOrAdvance,
      returnTo,
      returnState,
      setPyqTestsResultData,
      history,
      location.pathname,
      isCountdown,
      durationSeconds,
      timerSeconds,
      liveProgressIdentity,
    ]
  );

  const submitHomework = useCallback(
    async (updatedQuestions) => {
      const paperId =
        testDetails?.id ??
        testDetails?.paper_id ??
        questionsRef.current[currentIndex]?.paper_id ??
        null;

      if (!paperId) {
        alert("Paper id is required to submit this homework.");
        return;
      }

      const timeSpentSeconds = isCountdown
        ? Math.max(durationSeconds - timerSecondsRef.current, 0)
        : timerSecondsRef.current;

      const submitted = await submitHomeworkPaper(paperId, timeSpentSeconds);
      if (!submitted) {
        alert("Something went wrong while submitting. Please try again.");
        return;
      }

      const questionsForSummary = (updatedQuestions || []).map((question) =>
        Number(question.is_saved) === 1
          ? question
          : {
              ...question,
              userAnswerCollection: [],
              integer_answer: "",
            }
      );
      const homeworkTopics = Array.isArray(testDetails?.data)
        ? [
            ...new Set(
              testDetails.data.map((item) => item?.topic).filter(Boolean)
            ),
          ]
        : [];
      const homeworkName =
        homeworkTopics.join(", ") || testDetails?.subject || "Homework";
      const summary = {
        ...buildPyqPracticeSummary({
          questions: questionsForSummary,
          testDetails: {
            topic_name: homeworkName,
            returnTo,
            returnState,
            paper_id: paperId,
          },
          timeSpentSeconds,
          paperId,
        }),
        viewSolutionPath: "/homework-solution",
      };

      shouldPersistRef.current = false;
      clearExerciseLiveProgress(liveProgressIdentity);
      clearHomeworkResumeProgress(
        getHomeworkPaperId(testDetails) || getHomeworkPaperId(testData)
      );
      persistHomeworkPracticeSummary(summary);
      submittedSummaryRef.current = summary;

      const flowRoutes = getTopicFlowRoutes(HOMEWORK_FLOW, location.pathname);
      history.replace({
        pathname: flowRoutes.summary,
        state: { summary, type: "homework" },
      });
    },
    [
      testDetails,
      currentIndex,
      isCountdown,
      durationSeconds,
      liveProgressIdentity,
      returnTo,
      returnState,
      history,
      location.pathname,
    ]
  );

  const submitTopicExercise = useCallback(
    async (updatedQuestions, elapsedTime) => {
      const questionsForSummary = updatedQuestions.map((question) =>
        Number(question.is_saved) === 1
          ? question
          : {
              ...question,
              userAnswerCollection: [],
              integer_answer: "",
            }
      );
      const { localSummary } = buildTopicExerciseSubmitSummary(
        questionsForSummary,
        currentIndex,
        elapsedTime
      );

      shouldPersistRef.current = false;
      if (progressParams) {
        clearExerciseProgress(progressParams);
        markExerciseAsSubmitted(progressParams);
      }
      clearExerciseLiveProgress(liveProgressIdentity);

      persistExerciseSummary(localSummary);
      setUserTestsResultData({ ...testDetails, localSummary });
      recordSubmitAnalytics();
      submittedSummaryRef.current = localSummary;

      let coinsData = null;
      try {
        coinsData = await fetchSubmissionCoins({
          testType,
          testDetails,
          userPreference,
          topicData,
          userData,
        });
      } catch (error) {
        console.log(error);
      }

      if (coinsData && Number(coinsData.coins) > 0) {
        setEarnedCoins(coinsData.coins);
        setCoinReason(coinsData.description);
        setCoinPopup(true);
        return;
      }

      navigateToExerciseSummary(localSummary);
    },
    [
      currentIndex,
      progressParams,
      testDetails,
      testType,
      userPreference,
      topicData,
      userData,
      setUserTestsResultData,
      recordSubmitAnalytics,
      navigateToExerciseSummary,
      liveProgressIdentity,
    ]
  );

  const handleSubmitTest = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitModalOpen(false);

    const timedQuestions = commitElapsedTime(questions, currentIndex);
    const updatedQuestions = timedQuestions.map((question, index) =>
      index === currentIndex && hasExerciseAnswer(question)
        ? withCommittedExerciseAnswer(question)
        : question
    );
    const submittedQuestion = updatedQuestions[currentIndex];
    const elapsedTime = submittedQuestion?.totalTimeTaken ?? 0;

    startLoading();

    try {
      await saveQuestion(submittedQuestion);

      if (isPyq) {
        await submitPyq(updatedQuestions, elapsedTime);
      } else if (isHomework) {
        await submitHomework(updatedQuestions);
      } else {
        await submitTopicExercise(updatedQuestions, elapsedTime);
      }
    } catch (error) {
      console.log(error);
      if (!isPyq && !isHomework) {
        navigateToExerciseSummary(submittedSummaryRef.current);
      }
    } finally {
      finishLoading();
      setShowTimeUpModal(false);
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    questions,
    currentIndex,
    commitElapsedTime,
    saveQuestion,
    isPyq,
    isHomework,
    submitPyq,
    submitHomework,
    submitTopicExercise,
    navigateToExerciseSummary,
    startLoading,
    finishLoading,
  ]);

  submitHandlerRef.current = handleSubmitTest;

  useEffect(() => {
    return () => {
      if (!shouldPersistRef.current || !progressParams) return;
      persistExerciseProgress({
        ...progressParams,
        questions: questionsRef.current || [],
        currentQuestion: currentIndexRef.current || 0,
        timerSeconds: timerSecondsRef.current,
      });
    };
  }, [progressParams]);

  const handleExitTest = useCallback(() => {
    const updatedQuestions = commitElapsedTime(questions, currentIndex);
    const leavingQuestion = updatedQuestions[currentIndex];

    if (
      hasCommittedExerciseAnswer(leavingQuestion) ||
      leavingQuestion?.marked
    ) {
      saveQuestion(leavingQuestion);
    }
    persistProgress(updatedQuestions, currentIndex);

    setExitModalOpen(false);
    history.push({ pathname: returnTo, state: returnState });
  }, [
    questions,
    currentIndex,
    commitElapsedTime,
    saveQuestion,
    persistProgress,
    history,
    returnTo,
    returnState,
  ]);

  /* ======================= RENDER ======================= */
  if (isLoadingQuestions) {
    return (
      <div className="loading_center_container">
        <ClipLoader size={40} color="#C61C26" />
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="loading_center_container">
        <p>No questions available for this {flowLabel}.</p>
      </div>
    );
  }

  return (
    <div className="exam-wrapper">
      <ExerciseHeader
        timerSeconds={timerSeconds}
        isCountdown={isCountdown}
        onExitClick={() => setExitModalOpen(true)}
        onSubmit={() => setSubmitModalOpen(true)}
      />

      <div className="exam-body with-overview">
        <div className="exam_content_container">
          <ExerciseSectionTabs
            sections={sections}
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            questions={questions}
            currentIndex={currentIndex}
            stats={stats}
            onSelectQuestion={moveToQuestion}
            isPracticeMode={isPracticeMode}
          />

          <div className="exam-question-stage">
            {isQuestionDetailLoading && (
              <div className="exam-question-overlay" aria-busy="true">
                <ClipLoader size={36} color="#C61C26" />
              </div>
            )}
            <ExerciseQuestionCard
              key={currentQuestion?.qid ?? currentIndex}
              question={currentQuestion}
              questionNumber={currentIndex + 1}
              currentLang={currentLang}
              isPracticeMode={isPracticeMode}
              onToggleLanguage={handleToggleLanguage}
              onToggleBookmark={handleToggleBookmark}
              onSelectOption={handleSelectOption}
              onIntegerAnswerChange={handleIntegerAnswerChange}
            />
          </div>

          <ExerciseFooter
            isFirstQuestion={currentIndex === 0}
            isLastQuestion={currentIndex === questions.length - 1}
            hasAnswer={hasExerciseAnswer(currentQuestion)}
            busy={isSubmitting || isQuestionDetailLoading}
            isPracticeMode={isPracticeMode}
            answerChecked={currentQuestion?.answerChecked === true}
            onPrevious={handlePrevious}
            onMarkReview={handleMarkReview}
            onCheckAnswer={handlePracticeAction}
            onSaveNext={handleSaveAndNext}
          />
        </div>
      </div>

      <SubmitModal
        isOpen={isSubmitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        onSubmit={handleSubmitTest}
        title={`Submit ${flowLabel}?`}
        subtitle="Are you sure you want to submit your answers?"
        imageSrc={images.submit_icon}
        totalQuestions={questions.length}
        notAttempted={notAttemptedCount}
        attempted={attemptedCount}
        markedForReview={markedForReviewCount}
        cancelButtonProps={{
          title: "Cancel",
          variant: "outlined",
          color: "error",
          onClick: () => setSubmitModalOpen(false),
        }}
        submitButtonProps={{
          title: isSubmitting ? "Submitting..." : "Submit",
          variant: "contained",
          color: "error",
          onClick: handleSubmitTest,
        }}
      />

      <SubmitModal
        isOpen={isExitModalOpen}
        onClose={() => setExitModalOpen(false)}
        title={`Exit ${flowLabel}`}
        subtitle="Are you sure you want to exit? Your answers are saved and you can resume anytime."
        imageSrc={images.exit_modal_icon}
        totalQuestions={questions.length}
        notAttempted={notAttemptedCount}
        attempted={attemptedCount}
        markedForReview={markedForReviewCount}
        cancelButtonProps={{
          title: "Go Back",
          variant: "outlined",
          color: "error",
          onClick: () => setExitModalOpen(false),
        }}
        submitButtonProps={{
          title: "Exit",
          variant: "contained",
          color: "error",
          onClick: handleExitTest,
        }}
      />

      <Modal
        isOpen={showTimeUpModal}
        onClose={() => {}}
        height="14rem"
        width="32rem"
      >
        <div className="timeup-modal-content">
          <div className="timeup-modal-title">Time is up</div>
          <div className="timeup-modal-subtitle">
            The test will be auto submitted.
          </div>
        </div>
      </Modal>

      <RewardModal
        show={coinPopup}
        onHide={() => {
          setCoinPopup(false);
          navigateToExerciseSummary(submittedSummaryRef.current);
        }}
        earnMoreHandler={() => {
          setCoinPopup(false);
          setCoinDetailsPopup(true);
        }}
        earnedCoins={earnedCoins}
        reason={coinReason}
      />

      <CoinUsesModal
        show={coinDetailsPopup}
        onHide={() => {
          setCoinDetailsPopup(false);
          navigateToExerciseSummary(submittedSummaryRef.current);
        }}
        data={coinUsesPoints}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    exerciseData: state.userActivity.exerciseData,
    pyqTestData: state.pyqTestsData?.pyqTestData,
    homeworkTestDetails: state.HomeWorkSlice?.homeworkTestDetails,
    jeeMainOrAdvance: state.pyqTestsData?.pyqMainCurrentTab?.type,
    topicData: state.userActivity.topicData,
    subjectData: state.userActivity.subjectData,
    userPreference: state.userPreference,
    userData: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    startLoading: () => dispatch(uiActions.startLoading()),
    finishLoading: () => dispatch(uiActions.finishLoading()),
    setUserTestsResultData: (payload) =>
      dispatch(userTestsExercisesAction.setUserTestsResultData(payload)),
    setPyqTestsResultData: (payload) =>
      dispatch(userPyqTestsAction.setPyqTestsResultData(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ExerciseTestScreen);
